import * as React from "react";
import { TextBox } from "../../atoms/forms/TextBox";
import { ICommonInputsControls } from "./ICommonInputsControls";
import { useTranslation } from 'react-i18next';
import { InszValidator, Sex } from 'johnverbiest.belgian.insz';

// Additional data extracted from a valid INSZ number
export interface IRijksregisterData {
    text: string;           // The formatted INSZ string (e.g., "85.07.30-033.28")
    birthDate: Date | null; // Birth date extracted from the INSZ
    birthYear: number | null; // Birth year
    sex: Sex | null;        // Sex.Male, Sex.Female, or Sex.Unknown
    isBis: boolean;         // Whether it's a BIS number
}

// All the incoming values
interface IProps {
    onChangeWithData?: (value: string | null, isValid: boolean, data: IRijksregisterData | null) => void;
}

// Outgoing functions
interface IDispatch {
}

// Actual Component
export type AllProps = IProps & IDispatch & ICommonInputsControls<string>;
export const RijksregisterNummerInput = (props: AllProps) => {
    const { t } = useTranslation('common');
    
    // Internal error state for validation feedback
    const [internalError, setInternalError] = React.useState<[boolean, string] | undefined>(undefined);

    // Format the rijksregisternummer as: YY.MM.DD-XXX.XX
    const formatRijksregisternummer = (value: string): string => {
        // Remove all non-digit characters
        const digits = value.replace(/\D/g, '');
        
        // Limit to 11 digits
        const limited = digits.substring(0, 11);
        
        // Try to use the validator to format if we have a complete number
        if (limited.length === 11) {
            const result = InszValidator.Validate(limited);
            if (result.isValid && result.inszNumber) {
                const formatted = result.inszNumber.toFormattedString();
                if (formatted) {
                    return formatted;
                }
            }
        }
        
        // Fallback to manual formatting for incomplete numbers
        if (limited.length <= 2) {
            return limited;
        } else if (limited.length <= 4) {
            return `${limited.substring(0, 2)}.${limited.substring(2)}`;
        } else if (limited.length <= 6) {
            return `${limited.substring(0, 2)}.${limited.substring(2, 4)}.${limited.substring(4)}`;
        } else if (limited.length <= 9) {
            return `${limited.substring(0, 2)}.${limited.substring(2, 4)}.${limited.substring(4, 6)}-${limited.substring(6)}`;
        } else {
            return `${limited.substring(0, 2)}.${limited.substring(2, 4)}.${limited.substring(4, 6)}-${limited.substring(6, 9)}.${limited.substring(9)}`;
        }
    };

    // Validate rijksregisternummer using the Belgian INSZ validator
    const validateRijksregisternummer = (value: string | null): [boolean, string] => {
        if (!value || value.trim() === '') {
            if (props.required) {
                return [true, t('xIsRequired', { x: props.label || 'Rijksregisternummer' })];
            }
            return [false, ''];
        }

        // Remove formatting to get just digits
        const digits = value.replace(/\D/g, '');

        // Must be exactly 11 digits
        if (digits.length !== 11) {
            return [true, t('rijksregisterInvalidLength', 'Rijksregisternummer must be exactly 11 digits')];
        }

        // Use the Belgian INSZ validator
        const result = InszValidator.Validate(digits);
        
        if (!result.isValid) {
            return [true, t('rijksregisterInvalid', 'Invalid rijksregisternummer')];
        }

        return [false, ''];
    };

    // Handle change with formatting
    const handleChange = (value: string | null) => {
        if (!value) {
            setInternalError(undefined);
            props.onChange && props.onChange(null, !props.required);
            props.onChangeWithData && props.onChangeWithData(null, !props.required, null);
            return;
        }

        // Format the value
        const formatted = formatRijksregisternummer(value);
        
        // Validate
        const validationResult = validateRijksregisternummer(formatted);
        const hasError = validationResult[0];

        // Set internal error state for display
        if (hasError) {
            setInternalError(validationResult);
        } else {
            setInternalError(undefined);
        }

        // Extract additional data if valid
        let inszData: IRijksregisterData | null = null;
        if (!hasError) {
            const digits = formatted.replace(/\D/g, '');
            const result = InszValidator.Validate(digits);
            
            if (result.isValid && result.inszNumber) {
                const insz = result.inszNumber;
                inszData = {
                    text: formatted,
                    birthDate: insz.birthDate,
                    birthYear: insz.birthYear,
                    sex: insz.sex,
                    isBis: insz.isBis ?? false
                };
            }
        }

        // Call parent onChange with formatted value
        props.onChange && props.onChange(formatted, !hasError);
        
        // Call onChangeWithData with additional information
        props.onChangeWithData && props.onChangeWithData(formatted, !hasError, inszData);
    };

    // Custom validation that combines prop validation and rijksregister validation
    const customValidate = (value: string | null): [boolean, string] => {
        // First check prop validation
        if (props.onValidate) {
            const propValidation = props.onValidate(value);
            if (propValidation[0]) {
                return propValidation;
            }
        }

        // Then check rijksregister validation
        return validateRijksregisternummer(value);
    };

    return (
        <TextBox
            label={props.label || "Rijksregisternummer"}
            value={props.value}
            loading={props.loading}
            disabled={props.disabled}
            required={props.required}
            error={internalError || props.error}
            placeholder={props.placeholder || "YY.MM.DD-XXX.XX"}
            prevalidate={props.prevalidate}
            icon={props.icon}
            onChange={handleChange}
            onValidate={customValidate}
        />
    );
}

